import type { LocalizedText } from "@/math";

export interface QuestReward {
  xp: number;
  gold: number;
  items?: Record<string, number>;
  /** Flag set on the hero when the quest is completed (e.g. "bridge_fixed"). */
  flag?: string;
}

/**
 * What the hero has to do. `count` is the target for every kind:
 * defeat N monsters of a type, talk to an NPC (1), open a guarded chest (1),
 * reach a streak of N correct answers in battles, or answer N questions correctly in battles.
 */
export type QuestObjective =
  | { kind: "defeat"; monsterId: string; count: number }
  | { kind: "talk"; npcId: string; count: 1 }
  | { kind: "chest"; chestId: string; count: 1 }
  | { kind: "streak"; count: number }
  | { kind: "correct"; count: number };

export interface QuestDef {
  id: string;
  giver: string;
  title: LocalizedText;
  objective: QuestObjective;
  reward: QuestReward;
  requires?: string;
  offer: LocalizedText[];
  inProgress: LocalizedText[];
  complete: LocalizedText[];
}

const L = (pl: string, en: string): LocalizedText => ({ pl, en });

function quest(
  id: string,
  giver: string,
  title: [string, string],
  objective: QuestObjective,
  reward: QuestReward,
  texts: { offer: Array<[string, string]>; inProgress: [string, string]; complete: [string, string] },
  requires?: string,
): QuestDef {
  return {
    id,
    giver,
    title: L(...title),
    objective,
    reward,
    requires,
    offer: texts.offer.map(([p, e]) => L(p, e)),
    inProgress: [L(...texts.inProgress)],
    complete: [L(...texts.complete)],
  };
}

export const QUESTS: Record<string, QuestDef> = {
  // ---------------------------------------------------------------- Countinghollow
  q_blobbles: quest(
    "q_blobbles",
    "abacia",
    ["Pierwsze Błędy", "First Errors"],
    { kind: "defeat", monsterId: "blobble", count: 5 },
    { xp: 40, gold: 10, items: { wand_focus: 1 } },
    {
      offer: [
        [
          "Blobble mnożą się na łąkach wokół wioski. Pokonaj 5 z nich, a dam ci Różdżkę Skupienia — daje 5 sekund więcej na każde pytanie.",
          "Blobbles are multiplying in the meadows around the village. Defeat 5 of them and I will give you the Wand of Focus — 5 extra seconds on every question.",
        ],
      ],
      inProgress: [
        "Pokonaj 5 Blobble. Na razie: {progress}/{count}.",
        "Defeat 5 Blobbles. So far: {progress}/{count}.",
      ],
      complete: [
        "Brawo! Oto Różdżka Skupienia. Otwórz plecak (klawisz I) i załóż ją.",
        "Well done! Here is the Wand of Focus. Open your backpack (key I) and equip it.",
      ],
    },
  ),
  q_bridge: quest(
    "q_bridge",
    "milo",
    ["Naprawa mostu", "Bridge repair"],
    { kind: "defeat", monsterId: "off_by_one_imp", count: 3 },
    { xp: 60, gold: 20, flag: "bridge_fixed" },
    {
      offer: [
        [
          "Chochliki O-Jeden ukradły deski z mostu i teraz nikt nie przejdzie na łąki za rzeką!",
          "The Off-by-One Imps stole the planks from the bridge and now nobody can cross to the meadows!",
        ],
        [
          "Pokonaj 3 chochliki i odzyskaj deski. Naprawię most i sprowadzę do sklepu lepszy towar.",
          "Defeat 3 imps and recover the planks. I will fix the bridge and stock better goods.",
        ],
      ],
      inProgress: [
        "Deski: {progress}/{count}. Chochliki kręcą się nad rzeką.",
        "Planks: {progress}/{count}. The imps hang around the river.",
      ],
      complete: [
        "Wszystkie deski wróciły! Most naprawiony — droga na wschód stoi otworem. Zajrzyj do sklepu, mam nowe towary.",
        "All planks are back! The bridge is fixed — the road east is open. Check the shop, I have new goods.",
      ],
    },
  ),
  q_tin_knight: quest(
    "q_tin_knight",
    "abacia",
    ["Rycerz z Puszki", "The Tin Knight"],
    { kind: "defeat", monsterId: "tin_knight", count: 1 },
    { xp: 100, gold: 30, items: { sage_glasses: 1 } },
    {
      offer: [
        [
          "Za rzeką, przy stawie, grasuje Rycerz z Puszki. Pokonaj go, a dostaniesz Okulary Mędrca — jedna darmowa podpowiedź w każdej walce.",
          "Beyond the river, by the pond, roams the Tin Knight. Defeat him and you will get the Sage's Glasses — one free hint in every battle.",
        ],
      ],
      inProgress: [
        "Rycerz z Puszki wciąż straszy przy stawie za rzeką.",
        "The Tin Knight still haunts the pond beyond the river.",
      ],
      complete: [
        "Sardynka pokonana! Oto Okulary Mędrca. Załóż je w plecaku.",
        "The sardine is beaten! Here are the Sage's Glasses. Equip them in your backpack.",
      ],
    },
    "q_bridge",
  ),
  q_letter: quest(
    "q_letter",
    "antek",
    ["List do gajowego", "A letter for the forester"],
    { kind: "talk", npcId: "borys", count: 1 },
    { xp: 25, gold: 5 },
    {
      offer: [
        [
          "Napisałem list do gajowego Borysa, ale boję się iść sam do lasu. Zaniesiesz go? Jego chata stoi w Szepczącym Lesie, na wschód za łąkami.",
          "I wrote a letter to Forester Borys, but I am scared to go into the forest alone. Will you take it? His hut is in the Whispering Forest, east beyond the meadows.",
        ],
      ],
      inProgress: [
        "Borys mieszka w lesie na wschodzie — za mostem i łąkami, przy ścieżce.",
        "Borys lives in the forest to the east — past the bridge and the meadows, by the path.",
      ],
      complete: [
        "Dziękuję! Borys na pewno się ucieszył. Masz tu moje kieszonkowe.",
        "Thank you! Borys must have been glad. Here is my pocket money.",
      ],
    },
    "q_bridge",
  ),
  q_correct: quest(
    "q_correct",
    "quill",
    ["Zadanie od nauczyciela", "Homework from the wizard"],
    { kind: "correct", count: 25 },
    { xp: 50, gold: 20, items: { potion: 2 } },
    {
      offer: [
        [
          "Mam dla ciebie zadanie: odpowiedz poprawnie na 25 pytań w walkach z potworami. Trening się nie liczy!",
          "I have a task for you: answer 25 questions correctly in fights with monsters. Training does not count!",
        ],
      ],
      inProgress: [
        "Poprawne odpowiedzi w walkach: {progress}/{count}.",
        "Correct answers in fights: {progress}/{count}.",
      ],
      complete: [
        "Dwadzieścia pięć! Widzę, że ćwiczysz. Oto mikstury na drogę.",
        "Twenty-five! I can tell you practise. Here are potions for the road.",
      ],
    },
  ),

  // ---------------------------------------------------------------- Whispering Forest
  q_owls: quest(
    "q_owls",
    "borys",
    ["Nocne pytania", "Night questions"],
    { kind: "defeat", monsterId: "owl", count: 4 },
    { xp: 55, gold: 20, items: { potion: 2 } },
    {
      offer: [
        [
          "Sowy Sprawdziany nie dają spać całemu lasowi. Przegoń cztery, a dam ci mikstury z moich zapasów.",
          "The Quiz Owls keep the whole forest awake. Chase off four and I will give you potions from my stores.",
        ],
      ],
      inProgress: [
        "Sowy: {progress}/{count}. Siedzą na polanach i w gęstwinie.",
        "Owls: {progress}/{count}. They sit in the clearings and the thicket.",
      ],
      complete: [
        "Nareszcie cisza. Dziękuję! Oto mikstury.",
        "Quiet at last. Thank you! Here are the potions.",
      ],
    },
  ),
  q_snakes: quest(
    "q_snakes",
    "borys",
    ["Węże na ścieżce", "Snakes on the path"],
    { kind: "defeat", monsterId: "snake", count: 3 },
    { xp: 70, gold: 25, items: { amulet_time: 1 } },
    {
      offer: [
        [
          "Węże Ułamki oplotły ścieżkę na wybrzeże. Pokonaj trzy, a dostaniesz Amulet Czasu — 8 sekund więcej na każde pytanie.",
          "Fraction Snakes have coiled around the path to the coast. Defeat three and you will get the Amulet of Time — 8 extra seconds on every question.",
        ],
      ],
      inProgress: ["Węże: {progress}/{count}.", "Snakes: {progress}/{count}."],
      complete: [
        "Ścieżka wolna! Oto Amulet Czasu. Załóż go w plecaku.",
        "The path is clear! Here is the Amulet of Time. Equip it in your backpack.",
      ],
    },
    "q_owls",
  ),
  q_bear: quest(
    "q_bear",
    "borys",
    ["Niedźwiedź Bez Reszty", "The Remainder Bear"],
    { kind: "defeat", monsterId: "bear", count: 1 },
    { xp: 120, gold: 40, items: { hint_scroll: 3 } },
    {
      offer: [
        [
          "Niedźwiedź Bez Reszty mieszka w gęstwinie na wschodzie lasu. Jeśli go pokonasz, cały las odetchnie.",
          "The Remainder Bear lives in the thicket in the east of the forest. If you defeat it, the whole forest will breathe again.",
        ],
      ],
      inProgress: [
        "Niedźwiedź wciąż chodzi po wschodniej części lasu.",
        "The bear still roams the eastern part of the forest.",
      ],
      complete: [
        "Niedźwiedź pokonany! Masz tu zwoje z podpowiedziami — zasłużone.",
        "The bear is beaten! Here are hint scrolls — well earned.",
      ],
    },
    "q_snakes",
  ),

  // ---------------------------------------------------------------- Ruins & mine
  q_skeletons: quest(
    "q_skeletons",
    "tomik",
    ["Rachmistrze z ruin", "Counters of the ruins"],
    { kind: "defeat", monsterId: "skeleton_counter", count: 3 },
    { xp: 100, gold: 45, items: { hint_scroll: 2 } },
    {
      offer: [
        [
          "Szkielety Rachmistrze wciąż liczą w ruinach i nie pozwalają mi szukać kartek Księgi. Rozprosz trzy.",
          "The Skeleton Counters still count in the ruins and will not let me search for the Ledger's pages. Scatter three.",
        ],
      ],
      inProgress: [
        "Szkielety: {progress}/{count}. Kręcą się między kamieniami.",
        "Skeletons: {progress}/{count}. They wander among the stones.",
      ],
      complete: [
        "Mogę wreszcie szukać kartek. Weź te zwoje.",
        "I can finally search for the pages. Take these scrolls.",
      ],
    },
  ),
  q_streak: quest(
    "q_streak",
    "tomik",
    ["Osiem z rzędu", "Eight in a row"],
    { kind: "streak", count: 8 },
    { xp: 90, gold: 30 },
    {
      offer: [
        [
          "Prawdziwy rachmistrz nie myli się osiem razy z rzędu. Zdobądź serię ośmiu poprawnych odpowiedzi w walkach.",
          "A true reckoner does not slip eight times in a row. Get a streak of eight correct answers in fights.",
        ],
      ],
      inProgress: [
        "Najdłuższa seria od przyjęcia zadania: {progress}/{count}.",
        "Longest streak since taking the quest: {progress}/{count}.",
      ],
      complete: [
        "Osiem z rzędu! Wpisuję cię do Księgi Rachmistrzów.",
        "Eight in a row! I am writing you into the Book of Reckoners.",
      ],
    },
    "q_skeletons",
  ),
  q_moles: quest(
    "q_moles",
    "kazik",
    ["Krety w kopalni", "Moles in the mine"],
    { kind: "defeat", monsterId: "mole", count: 3 },
    { xp: 60, gold: 25 },
    {
      offer: [
        [
          "Krety Dzielniki ryją pod kopalnią. Pokonaj trzy, zanim zawali się wejście.",
          "Divisor Moles are digging under the mine. Defeat three before the entrance collapses.",
        ],
      ],
      inProgress: ["Krety: {progress}/{count}.", "Moles: {progress}/{count}."],
      complete: ["Kopalnia uratowana! Masz tu zapłatę.", "The mine is saved! Here is your pay."],
    },
  ),
  q_pickaxe: quest(
    "q_pickaxe",
    "kazik",
    ["Kilof Kazika", "Kazik's pickaxe"],
    { kind: "chest", chestId: "chest_pickaxe", count: 1 },
    { xp: 80, gold: 30 },
    {
      offer: [
        [
          "Mój kilof leży w skrzyni przy wejściu do kopalni. Pilnuje jej jaszczur. Otwórz ją — kilof jest mój, ale kolczuga ze skrzyni jest twoja.",
          "My pickaxe is in the chest by the mine entrance. A lizard guards it. Open it — the pickaxe is mine, but the chainmail in the chest is yours.",
        ],
      ],
      inProgress: [
        "Skrzynia z kilofem stoi przy wejściu do kopalni, na południe stąd.",
        "The chest with the pickaxe is by the mine entrance, south of here.",
      ],
      complete: [
        "Mój kilof! Dziękuję. Kolczugę zatrzymaj — w kopalni już jej nie potrzebuję.",
        "My pickaxe! Thank you. Keep the chainmail — I will not need it in the mine.",
      ],
    },
    "q_moles",
  ),

  // ---------------------------------------------------------------- Cannery Coast
  q_tin_patrol: quest(
    "q_tin_patrol",
    "mila",
    ["Blaszany patrol", "Tin patrol"],
    { kind: "defeat", monsterId: "tin_knight", count: 3 },
    { xp: 130, gold: 50, items: { hint_scroll: 3 } },
    {
      offer: [
        [
          "Trzech Rycerzy z Puszki patroluje plażę. Pokonaj ich, a wpiszę cię do księgi rycerzy.",
          "Three Tin Knights patrol the beach. Defeat them and I will enter you in the book of knights.",
        ],
      ],
      inProgress: ["Rycerze z Puszki: {progress}/{count}.", "Tin Knights: {progress}/{count}."],
      complete: [
        "Rycerzu! Oto zwoje z kapitańskiej skrzyni.",
        "Knight! Here are scrolls from the captain's chest.",
      ],
    },
  ),
  q_octopus: quest(
    "q_octopus",
    "marta",
    ["Plątanina sieci", "Tangled nets"],
    { kind: "defeat", monsterId: "octopus", count: 4 },
    { xp: 80, gold: 35, items: { big_potion: 2 } },
    {
      offer: [
        [
          "Ośmiornice Osiem plączą moje sieci. Pokonaj cztery, a podzielę się zapasami.",
          "The Octo-Eights tangle my nets. Defeat four and I will share my supplies.",
        ],
      ],
      inProgress: [
        "Ośmiornice: {progress}/{count}. Siedzą przy samej wodzie.",
        "Octopuses: {progress}/{count}. They sit right by the water.",
      ],
      complete: ["Sieci wolne! Oto duże mikstury.", "The nets are free! Here are big potions."],
    },
  ),
  q_sergeant: quest(
    "q_sergeant",
    "marta",
    ["Sierżant Sardynka", "The Sardine Sergeant"],
    { kind: "defeat", monsterId: "sardine_sergeant", count: 1 },
    { xp: 250, gold: 100, items: { big_potion: 2 } },
    {
      offer: [
        [
          "Sierżant Sardynka obozuje na wschodnim końcu plaży. To dowódca — nie da się od niego uciec, więc weź mikstury i idź, gdy będziesz gotowy.",
          "The Sardine Sergeant camps at the east end of the beach. He is the commander — there is no running away from him, so take potions and go when you are ready.",
        ],
      ],
      inProgress: [
        "Sierżant czeka na wschodnim krańcu plaży.",
        "The Sergeant waits at the east end of the beach.",
      ],
      complete: [
        "Legion bez sierżanta to tylko puszki! Wybrzeże jest bezpieczne.",
        "A Legion without its sergeant is just tins! The coast is safe.",
      ],
    },
    "q_octopus",
  ),
};

export function getQuest(id: string): QuestDef {
  const q = QUESTS[id];
  if (!q) throw new Error(`Unknown quest: ${id}`);
  return q;
}
